import {
  Button,
  Radio,
  Select,
  TextInput,
  Textarea,
  Tooltip,
} from "flowbite-react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "../Common/Loader";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../Reducers/store";
import { Warranty, WarrantyStatusEnum } from "../../Models/Warranty";
import { WarrantyPartsReplaced } from "../../Models/WarrantyPartsReplaced";
import { WarrantyLabor } from "../../Models/WarrantyLabor";
import WarrantyPartsTable from "./WarrantyPartsTable";
import { WarrantyPhoto } from "../../Models/WarrantyPhoto";
import { formatDateInputs, onlyNumberInput } from "../../Utils";
import WarrantyLaborTable from "./WarrantyLaborTable";
import WarrantyPhotosTable from "./WarrantyPhotosTable";
import WarrantyApi from "../../Api/WarrantyApi";
import EditWarrantyPartModal from "./EditWarrantyPartModal";
import EditWarrantyLaborModal from "./EditWarrantyLaborModal";
import { WarrantyPhotosToUpload } from "../../Models/WarrantyPhotosToUpload";
import EditWarrantyPhotoModal from "./EditWarrantyPhotoModal";
import WarrantyChangeLogPanel from "./WarrantyChangeLogPanel";
import { WarrantyChangeLogEntry } from "../../Models/WarrantyChangeLog";
import { UserLevelNames } from "../../Models/UserLevel";
import ManufacturerApi from "../../Api/ManufacturerApi";
import { Manufacturer } from "../../Models/Manufacturer";
import {
  WARRANTY_DESCRIPTION_MAX_LENGTH,
  WARRANTY_DESCRIPTION_MIN_LENGTH,
  truncateToMaxLength,
  warrantyClaimStringFieldMaxLength,
  WARRANTY_PHOTO_NOTES_MAX_LENGTH,
} from "../../Constants/warrantyFieldLimits";
import { User } from "../../Models/User";
import { Client } from "../../Models/Client";
import type { State as UsState } from "../../Models/State";
import ClientApi from "../../Api/ClientApi";
import CommonApi from "../../Api/CommonApi";

type ClientStateNode = {
  code?: string;
  name?: string;
  Code?: string;
  Name?: string;
};

type ClientWithPascalState = Client & { State?: ClientStateNode | null };

/** Read state code/name from API client whether JSON used `state` or `State` / `code` or `Code`. */
function stateTextFromClient(c: Client | null | undefined): string {
  if (!c) return "";
  const raw = (c.state ?? (c as ClientWithPascalState).State) as
    | ClientStateNode
    | null
    | undefined;
  if (!raw) return "";
  const code = raw.code ?? raw.Code ?? "";
  const name = raw.name ?? raw.Name ?? "";
  return (code || name).trim();
}

let statesListPromise: Promise<UsState[]> | null = null;

function loadStatesListOnce(): Promise<UsState[]> {
  if (!statesListPromise) {
    statesListPromise = CommonApi.getStates().then((r) => r.data);
  }
  return statesListPromise;
}

/** When nested `state` is missing but `stateId` is set, resolve code/name from /api/common/states. */
function fillSellingStateFromClientStateId(
  c: Client | null | undefined,
  setForm: React.Dispatch<React.SetStateAction<Warranty>>
): void {
  if (!c?.stateId) return;
  if (stateTextFromClient(c)) return;
  loadStatesListOnce()
    .then((states) => {
      const row = states.find((s) => s.id === c.stateId);
      const text = (row?.code ?? row?.name ?? "").trim();
      if (!text) return;
      setForm((prev) => {
        if (prev.sellingState?.trim()) return prev;
        return {
          ...prev,
          sellingState: truncateToMaxLength(
            warrantyClaimStringFieldMaxLength.sellingState,
            text
          ),
        };
      });
    })
    .catch(() => {});
}

/**
 * Prefill new warranty drafts from profile + linked client (selling dealer).
 * `clientOverride` is used when the user has clientId but /api/auth did not include client (e.g. stale session).
 */
function mergeLoggedInUserIntoNewWarrantyDraft(
  user: User,
  draft: Warranty,
  clientOverride?: Client | null
): Warranty {
  const next = { ...draft, userId: user.id };
  const displayName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const c = clientOverride ?? user.client ?? null;

  if (!draft.filledOutByName?.trim() && displayName) {
    next.filledOutByName = truncateToMaxLength(
      warrantyClaimStringFieldMaxLength.filledOutByName,
      displayName
    );
  }
  if (!draft.filledOutByEmail?.trim() && user.emailAddress) {
    next.filledOutByEmail = truncateToMaxLength(
      warrantyClaimStringFieldMaxLength.filledOutByEmail,
      user.emailAddress
    );
  }
  if (!draft.sellingDealerName?.trim() && c?.name) {
    next.sellingDealerName = truncateToMaxLength(
      warrantyClaimStringFieldMaxLength.sellingDealerName,
      c.name
    );
  }
  if (!draft.sellingDealerAddress?.trim() && c?.address) {
    next.sellingDealerAddress = truncateToMaxLength(
      warrantyClaimStringFieldMaxLength.sellingDealerAddress,
      c.address
    );
  }
  if (!draft.sellingCity?.trim() && c?.city) {
    next.sellingCity = truncateToMaxLength(
      warrantyClaimStringFieldMaxLength.sellingCity,
      c.city
    );
  }
  const stateStr = stateTextFromClient(c);
  if (!draft.sellingState?.trim() && stateStr) {
    next.sellingState = truncateToMaxLength(
      warrantyClaimStringFieldMaxLength.sellingState,
      stateStr
    );
  }
  if (!draft.sellingZip?.trim() && c?.zip) {
    next.sellingZip = truncateToMaxLength(
      warrantyClaimStringFieldMaxLength.sellingZip,
      c.zip
    );
  }
  return next;
}

export default function WarrantyClaim() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);
  const [warrantyData, setWarrantyData] = useState<Warranty | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showWarrantyPartModal, setShowWarrantyPartModal] =
    useState<boolean>(false);
  const [selectedWarrantyPart, setSelectedWarrantyPart] =
    useState<WarrantyPartsReplaced | null>();
  const [showWarrantyLaborModal, setShowWarrantyLaborModal] =
    useState<boolean>(false);
  const [selectedWarrantyLabor, setSelectedWarrantyLabor] =
    useState<WarrantyLabor | null>();
  const [showWarrantyPhotoModal, setShowWarrantyPhotoModal] =
    useState<boolean>(false);
  const [selectedWarrantyPhoto, setSelectedWarrantyPhoto] =
    useState<WarrantyPhoto | null>();
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [files, setFiles] = useState<WarrantyPhotosToUpload[]>([]);
  const [manufacturerData, setManufacturerData] = useState<
    Manufacturer[] | null
  >();
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [changeLogEntries, setChangeLogEntries] = useState<
    WarrantyChangeLogEntry[]
  >([]);

  const hasStaffWarrantyAccess = () =>
    userModel?.userLevelId === UserLevelNames.Admin ||
    userModel?.userLevelId === UserLevelNames.Employee;

  const loadChangeLog = (warrantyId: number) => {
    if (!hasStaffWarrantyAccess()) return;
    WarrantyApi.getWarrantyChangeLog(warrantyId)
      .then((res) => setChangeLogEntries(res.data))
      .catch(() => setChangeLogEntries([]));
  };

  const getManufacturer = () => {
    setIsLoading(true);
    ManufacturerApi.getManufacturerListWG()
      .then((res) => {
        setManufacturerData(res.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setTimeout(() => {
      getManufacturer();
    }, 100);
  }, []);

  const [warrantyFormData, setWarrantyFormData] = useState<Warranty>({
    id: 0,
    date: null,
    userId: userModel?.id ?? null,
    manufacturerId: 0,
    sellingDealerName: null,
    sellingDealerAddress: null,
    sellingCity: null,
    sellingState: null,
    sellingZip: null,
    customerName: null,
    customerAddress: null,
    customerCity: null,
    customerState: null,
    customerZip: null,
    model: null,
    serialNumber: null,
    datePurchase: null,
    dateFailure: null,
    acresHrsOperated: null,
    tractorMake: null,
    tractorModel: null,
    dateOfRepair: null,
    attachmentsAdded: null,
    description: null,
    statusId: 1,
    creditNumber: null,
    creditAmount: null,
    filledOutByName: null,
    distributorName: null,
    statusChangeDate: null,
    returnPartsUps: true,
    returnPartsComercial: null,
    returnPartsCompanyTruck: null,
    filledOutByEmail: null,

    manufacturer: null,
    warrantyLabors: [],
    warrantyPartsReplaceds: [],
    warrantyPhotos: [],
  });
  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const maxLen = warrantyClaimStringFieldMaxLength[name];
    const next =
      maxLen != null
        ? truncateToMaxLength(maxLen, value)
        : name === "acresHrsOperated"
          ? truncateToMaxLength(10, value)
          : value;
    setWarrantyFormData((prevData) => ({ ...prevData, [name]: next }));
  };
  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const v =
      name === "description"
        ? truncateToMaxLength(WARRANTY_DESCRIPTION_MAX_LENGTH, value)
        : value;
    if (name === "description") {
      if (v.length > 0 && v.length < WARRANTY_DESCRIPTION_MIN_LENGTH) {
        setDescriptionError(
          `Description must be at least ${WARRANTY_DESCRIPTION_MIN_LENGTH} characters. Please describe the issue in detail.`
        );
      } else {
        setDescriptionError(null);
      }
    }
    setWarrantyFormData((prevData) => ({ ...prevData, [name]: v }));
  };
  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setWarrantyFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  const handleChangeInputReturnMethod = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setWarrantyFormData((prevData) => ({ ...prevData, returnPartsUps: false }));
    setWarrantyFormData((prevData) => ({
      ...prevData,
      returnPartsComercial: false,
    }));
    setWarrantyFormData((prevData) => ({
      ...prevData,
      returnPartsCompanyTruck: false,
    }));

    const { name, checked } = e.target;
    setWarrantyFormData((prevData) => ({ ...prevData, [name]: checked }));
  };

  const [warrantyPartsList, setWarrantyPartsList] = useState<
    WarrantyPartsReplaced[]
  >([]);
  function addUpdateWarrantyPart(part: WarrantyPartsReplaced) {
    setButtonDisabled(true);
    if (part.id > 0) {
      try {
        setIsLoading(true);
        WarrantyApi.addUpdateWarrantyPart(part)
          .then((res) => {
            if (res.data.success) {
              setShowWarrantyPartModal(false);
              getWarrantyParts();
            } else {
              toast.error(res.data.message);
            }
          })
          .catch((err) => {
            toast.error("Error: " + err);
            setIsLoading(false);
          });
        setIsLoading(false);
        setButtonDisabled(false);
        return;
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        setButtonDisabled(false);
      }
    } else {
      if (selectedWarrantyPart) {
        setWarrantyPartsList((warrantyPartsList) => [
          ...warrantyPartsList.filter((x) => x !== selectedWarrantyPart),
          part,
        ]);
      } else {
        setWarrantyPartsList((warrantyPartsList) => [
          ...warrantyPartsList,
          part,
        ]);
      }
      setShowWarrantyPartModal(false);
    }
    setButtonDisabled(false);
  }
  function deleteWarrantyPart(part: WarrantyPartsReplaced) {
    const shouldRemove = window.confirm(
      "Are you sure you want to delete this?"
    );
    if (shouldRemove) {
      if (part.id > 0) {
        WarrantyApi.deleteWarrantyPart(part.id)
          .then((res) => {
            if (res.data.success) {
              getWarrantyParts();
            } else {
              toast.success("Error while deleting part");
            }
          })
          .catch((err) => {
            toast.error("Error: " + err);
          });
      } else {
        setWarrantyPartsList(warrantyPartsList.filter((x) => x !== part));
      }
    }
  }

  const [warrantyLaborList, setWarrantyLaborList] = useState<WarrantyLabor[]>(
    []
  );
  function addUpdateWarrantyLabor(labor: WarrantyLabor) {
    setButtonDisabled(true);
    if (labor.id > 0) {
      try {
        setIsLoading(true);
        WarrantyApi.addUpdateWarrantyLabor(labor)
          .then((res) => {
            if (res.data.success) {
              setShowWarrantyLaborModal(false);
              getWarrantyLabors();
            } else {
              toast.error(res.data.message);
            }
          })
          .catch((err) => {
            toast.error("Error: " + err);
            setIsLoading(false);
          });
        setIsLoading(false);
        setButtonDisabled(false);
        return;
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        setButtonDisabled(false);
      }
    } else {
      if (selectedWarrantyLabor) {
        setWarrantyLaborList((warrantyLaborList) => [
          ...warrantyLaborList.filter((x) => x !== selectedWarrantyLabor),
          labor,
        ]);
      } else {
        setWarrantyLaborList((warrantyLaborList) => [
          ...warrantyLaborList,
          labor,
        ]);
      }
      setShowWarrantyLaborModal(false);
    }
    setButtonDisabled(false);
  }
  function deleteWarrantyLabor(labor: WarrantyLabor) {
    const shouldRemove = window.confirm(
      "Are you sure you want to delete this?"
    );
    if (shouldRemove) {
      if (labor.id > 0) {
        WarrantyApi.deleteWarrantyLabor(labor.id)
          .then((res) => {
            if (res.data.success) {
              getWarrantyLabors();
            } else {
              toast.success("Error while deleting labor");
            }
          })
          .catch((err) => {
            toast.error("Error: " + err);
          });
      } else {
        setWarrantyLaborList(warrantyLaborList.filter((x) => x !== labor));
      }
    }
  }

  const [warrantyPhotoList, setWarrantyPhotoList] = useState<WarrantyPhoto[]>(
    []
  );
  function addUpdateWarrantyPhoto(
    photo: WarrantyPhoto,
    photoFile: File | null | undefined
  ) {
    if (!photoFile) {
      toast.error("Unable to add item. Missing File.");
    }
    setButtonDisabled(true);
    if (photo.id > 0) {
      try {
        setIsLoading(true);
        WarrantyApi.addUpdateWarrantyPhoto(photo)
          .then((res) => {
            if (res.data.success) {
              setShowWarrantyPhotoModal(false);
              getWarrantyPhotos();
            } else {
              toast.error(res.data.message);
            }
          })
          .catch((err) => {
            toast.error("Error: " + err);
            setIsLoading(false);
          });
        setIsLoading(false);
        setButtonDisabled(false);
        return;
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        setButtonDisabled(false);
      }
    } else {
      if (selectedWarrantyPhoto) {
        setWarrantyPhotoList((warrantyPhotoList) => [
          ...warrantyPhotoList.filter((x) => x !== selectedWarrantyPhoto),
          photo,
        ]);
        const editPhotoIndex = files.findIndex((x) => x.url === photo.photoUrl);
        if (editPhotoIndex >= 0) {
          files[editPhotoIndex].notes = photo.notes;
        }
      } else {
        if (!photoFile) {
          toast.error("Unable to add item. Missing File.");
        } else {
          const objectUrl = URL.createObjectURL(photoFile);
          const newPhotoToUpload = {
            file: photoFile,
            notes: photo.notes,
            url: objectUrl,
          };
          setFiles((files) => [...files, newPhotoToUpload]);

          const newPhoto = {
            id: 0,
            warrantyId: null,
            photoUrl: objectUrl,
            notes: photo.notes,
            dateCreated: null,
          };
          setWarrantyPhotoList((list) => [...list, newPhoto]);
        }
      }
      setShowWarrantyPhotoModal(false);
    }
    setButtonDisabled(false);
  }
  function deleteWarrantyPhoto(image: WarrantyPhoto) {
    const shouldRemove = window.confirm(
      "Are you sure you want to delete this?"
    );
    if (shouldRemove) {
      if (image.id > 0) {
        WarrantyApi.deleteWarrantyPhoto(image.id)
          .then((res) => {
            if (res.data.success) {
              getWarrantyPhotos();
            } else {
              toast.success("Error while deleting photo");
            }
          })
          .catch((err) => {
            toast.error("Error: " + err);
          });
      } else {
        setWarrantyPhotoList(warrantyPhotoList.filter((x) => x !== image));
        setFiles((files) => files.filter((x) => x.url !== image.photoUrl));
      }
    }
  }

  const getWarranty = () => {
    if (id) {
      setIsLoading(true);
      WarrantyApi.getWarranty(id)
        .then((res) => {
          setWarrantyData(res.data);
          setWarrantyPartsList(res.data.warrantyPartsReplaceds);
          setWarrantyLaborList(res.data.warrantyLabors);
          setWarrantyPhotoList(res.data.warrantyPhotos);
          setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    } else {
      setWarrantyData(null);
    }
  };

  useEffect(() => {
    getWarranty();
    if (id && Number(id) > 0) {
      loadChangeLog(Number(id));
    } else {
      setChangeLogEntries([]);
    }
  }, [id]);

  const emptyNewWarrantyTemplate = (userId: number | null): Warranty => ({
    id: 0,
    date: null,
    userId,
    manufacturerId: 0,
    sellingDealerName: null,
    sellingDealerAddress: null,
    sellingCity: null,
    sellingState: null,
    sellingZip: null,
    customerName: null,
    customerAddress: null,
    customerCity: null,
    customerState: null,
    customerZip: null,
    model: null,
    serialNumber: null,
    datePurchase: null,
    dateFailure: null,
    acresHrsOperated: null,
    tractorMake: null,
    tractorModel: null,
    dateOfRepair: null,
    attachmentsAdded: null,
    description: null,
    statusId: 1,
    creditNumber: null,
    creditAmount: null,
    filledOutByName: null,
    distributorName: null,
    statusChangeDate: null,
    returnPartsUps: true,
    returnPartsComercial: null,
    returnPartsCompanyTruck: null,
    filledOutByEmail: null,

    manufacturer: null,
    warrantyLabors: [],
    warrantyPartsReplaceds: [],
    warrantyPhotos: [],
  });

  useEffect(() => {
    if (warrantyData) {
      setWarrantyFormData({ ...warrantyData } as Warranty);
      return;
    }
    setWarrantyFormData(emptyNewWarrantyTemplate(null));
  }, [warrantyData]);

  useEffect(() => {
    if (warrantyData || !userModel) return;

    const embeddedClient = userModel.client;
    if (embeddedClient || !userModel.clientId) {
      setWarrantyFormData((prev) =>
        mergeLoggedInUserIntoNewWarrantyDraft(
          userModel,
          prev,
          embeddedClient ?? undefined
        )
      );
      fillSellingStateFromClientStateId(
        embeddedClient ?? null,
        setWarrantyFormData
      );
      return;
    }

    let cancelled = false;
    ClientApi.getClient(String(userModel.clientId))
      .then((res) => {
        if (cancelled) return;
        setWarrantyFormData((prev) =>
          mergeLoggedInUserIntoNewWarrantyDraft(userModel, prev, res.data)
        );
        fillSellingStateFromClientStateId(res.data, setWarrantyFormData);
      })
      .catch(() => {
        if (cancelled) return;
        setWarrantyFormData((prev) =>
          mergeLoggedInUserIntoNewWarrantyDraft(userModel, prev, null)
        );
      });

    return () => {
      cancelled = true;
    };
  }, [userModel, warrantyData]);

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setButtonDisabled(true);
    let warrantyId = 0;
    try {
      setIsLoading(true);
      if (!warrantyFormData.acresHrsOperated) {
        warrantyFormData.acresHrsOperated = 0;
      }
      if (!warrantyFormData.datePurchase) {
        warrantyFormData.datePurchase = null;
      }
      if (!warrantyFormData.dateFailure) {
        warrantyFormData.dateFailure = null;
      }
      if (!warrantyFormData.dateOfRepair) {
        warrantyFormData.dateOfRepair = null;
      }
      const hasUploadedPhotos = warrantyPhotoList.some((photo) => photo.id > 0);
      const hasNewPhotos = files.length > 0;

      if (!hasUploadedPhotos && !hasNewPhotos) {
        toast.error("At least one photo is required.");
        setIsLoading(false);
        setButtonDisabled(false);
        return;
      }
      if (
        !warrantyFormData.description ||
        warrantyFormData.description.length < WARRANTY_DESCRIPTION_MIN_LENGTH
      ) {
        toast.error(
          `Description must be at least ${WARRANTY_DESCRIPTION_MIN_LENGTH} characters. Please try to describe the issue in detail.`
        );
        setButtonDisabled(false);
        setIsLoading(false);
        return;
      }
      if (
        warrantyFormData.description.length > WARRANTY_DESCRIPTION_MAX_LENGTH
      ) {
        toast.error(
          `Description cannot exceed ${WARRANTY_DESCRIPTION_MAX_LENGTH} characters.`
        );
        setButtonDisabled(false);
        setIsLoading(false);
        return;
      }
      await WarrantyApi.addUpdateWarranty(warrantyFormData)
        .then(async (res) => {
          if (res.data.success) {
            warrantyId = Number(res.data.affectedEntityIdentifier);
            if (warrantyId > 0) {
              for (let index = 0; index < warrantyPartsList.length; ++index) {
                const element = warrantyPartsList[index];
                element.warrantyId = warrantyId;
                await WarrantyApi.addUpdateWarrantyPart(element)
                  .then((res) => {
                    if (!res.data.success) {
                      toast.error(res.data.message);
                    }
                  })
                  .catch((err) => {
                    toast.error("Error: " + err);
                  });
              }
              for (let index = 0; index < warrantyLaborList.length; ++index) {
                const element = warrantyLaborList[index];
                element.warrantyId = warrantyId;
                await WarrantyApi.addUpdateWarrantyLabor(element)
                  .then((res) => {
                    if (!res.data.success) {
                      toast.error(res.data.message);
                    }
                  })
                  .catch((err) => {
                    toast.error("Error: " + err);
                  });
              }
              for (let index = 0; index < files.length; ++index) {
                const file = files[index];
                const fd = new FormData();
                fd.append("file", file.file);
                fd.append("warrantyId", warrantyId.toString());
                fd.append(
                  "notes",
                  truncateToMaxLength(
                    WARRANTY_PHOTO_NOTES_MAX_LENGTH,
                    file.notes ?? ""
                  )
                );
                await WarrantyApi.uploadWarrantyImage(fd)
                  .then((res) => {
                    if (res.data.success) {
                      // imageUpdated = true;
                    }
                  })
                  .catch((err) => {
                    toast.error("Error: " + err);
                  });
              }
              // Only submit warranty request for new warranties (not updates)
              if (!id || id === "0") {
                await WarrantyApi.submitWarrantyRequest(warrantyId.toString())
                  .then((res) => {
                    if (res.data.success) {
                      toast.success("Claim Submitted");
                      navigate(`/`);
                    } else {
                      toast.error(res.data.message);
                    }
                  })
                  .catch((err) => {
                    toast.error("Error: " + err);
                  });
              } else {
                toast.success("Warranty Updated");
                if (hasStaffWarrantyAccess()) {
                  loadChangeLog(warrantyId);
                  navigate(`/warranty/claim/${warrantyId}`);
                } else {
                  navigate(`/`);
                }
              }
            }
          } else {
            toast.error(res.data.message);
          }
        })
        .catch((err) => {
          toast.error("Error: " + err);
        });
      setIsLoading(false);
      setButtonDisabled(false);

      return;
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };

  const getWarrantyParts = () => {
    if (userModel && id) {
      try {
        setIsLoading(true);
        return WarrantyApi.getWarrantyPartsReplaceds(id)
          .then((res) => {
            setWarrantyPartsList(warrantyPartsList.filter((x) => x.id === 0));
            setWarrantyPartsList((warrantyPartsList) => [
              ...res.data,
              ...warrantyPartsList,
            ]);
            setIsLoading(false);
          })
          .catch((err) => {
            toast.error("Error");
            setIsLoading(false);
          });
      } catch (error) {
        console.error(error);
        toast.error("Error");
        setIsLoading(false);
      }
    }
  };

  const getWarrantyLabors = () => {
    if (userModel && id) {
      try {
        setIsLoading(true);
        return WarrantyApi.getWarrantyLabors(id)
          .then((res) => {
            setWarrantyLaborList(warrantyLaborList.filter((x) => x.id === 0));
            setWarrantyLaborList((warrantyLaborList) => [
              ...res.data,
              ...warrantyLaborList,
            ]);
            setIsLoading(false);
          })
          .catch((err) => {
            toast.error("Error");
            setIsLoading(false);
          });
      } catch (error) {
        console.error(error);
        toast.error("Error");
        setIsLoading(false);
      }
    }
  };

  const getWarrantyPhotos = () => {
    if (userModel && id) {
      try {
        setIsLoading(true);
        return WarrantyApi.getWarrantyPhotos(id)
          .then((res) => {
            setWarrantyPhotoList(warrantyPhotoList.filter((x) => x.id === 0));
            setWarrantyPhotoList((warrantyPhotoList) => [
              ...res.data,
              ...warrantyPhotoList,
            ]);
            setIsLoading(false);
          })
          .catch((err) => {
            toast.error("Error");
            setIsLoading(false);
          });
      } catch (error) {
        console.error(error);
        toast.error("Error");
        setIsLoading(false);
      }
    }
  };

  const editWarrantyPartsReplaceds = (part: WarrantyPartsReplaced | null) => {
    setSelectedWarrantyPart(part);
    setShowWarrantyPartModal(true);
  };

  const editWarrantyLabor = (labor: WarrantyLabor | null) => {
    setSelectedWarrantyLabor(labor);
    setShowWarrantyLaborModal(true);
  };

  const editWarrantyPhoto = (photo: WarrantyPhoto | null) => {
    setSelectedWarrantyPhoto(photo);
    setShowWarrantyPhotoModal(true);
  };
  function hasStaffWarrantyAccessForStatus() {
    return hasStaffWarrantyAccess();
  }

  return (
    <div className="w-full">
      {isLoading && <Loader />}
      <div className="w-full p-4">
        <div className="mx-1 flex items-end">
          <div className="flex flex-1">
            <h1 className="text-gray-900 font-bold text-4xl">
              Submit Warranty Claim
            </h1>
          </div>
        </div>
      </div>

      <div className="h-[calc(100vh-142px)] md:h-[calc(100vh-210px)] overflow-y-auto">
        <div className="container mx-auto p-2">
          <div className="md:flex no-wrap md:-mx-2 ">
            <div className="w-full mx-2">
              <div className="bg-white p-3">
                <form onSubmit={onSubmit}>
                  <div className="text-gray-600">
                    <fieldset>
                      <legend className="text-black font-bold bg-gray-100 w-full rounded-t-md px-1">
                        Information
                      </legend>
                      <hr className="bg-gray-300 h-[2px]"></hr>
                      <div className="flex flex-col md:flex-none md:grid md:grid-cols-[50%_50%] text-xl">
                        <div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg lg:h-[42px]">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Status:
                              </div>
                            </div>
                            <div className="flex flex-col lg:flex-row gap-1 lg:gap-5">
                              <div className="flex items-center gap-2">
                                <Radio
                                  id="new"
                                  name="statusId"
                                  value={WarrantyStatusEnum.New.toString()}
                                  checked={
                                    Number(warrantyFormData.statusId) ===
                                    WarrantyStatusEnum.New
                                  }
                                  onChange={handleChangeInput}
                                  disabled={!hasStaffWarrantyAccessForStatus()}
                                />
                                <div
                                  className={`font-bold content-center text-sm ${
                                    !hasStaffWarrantyAccessForStatus() ? "text-gray-400" : ""
                                  }`}
                                >
                                  New
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Radio
                                  id="pending"
                                  name="statusId"
                                  value={WarrantyStatusEnum.Pending.toString()}
                                  checked={
                                    Number(warrantyFormData.statusId) ===
                                    WarrantyStatusEnum.Pending
                                  }
                                  onChange={handleChangeInput}
                                  disabled={!hasStaffWarrantyAccessForStatus()}
                                />
                                <div
                                  className={`font-bold content-center text-sm ${
                                    !hasStaffWarrantyAccessForStatus() ? "text-gray-400" : ""
                                  }`}
                                >
                                  Pending
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Radio
                                  id="approved"
                                  name="statusId"
                                  value={WarrantyStatusEnum.Approved.toString()}
                                  checked={
                                    Number(warrantyFormData.statusId) ===
                                    WarrantyStatusEnum.Approved
                                  }
                                  onChange={handleChangeInput}
                                  disabled={!hasStaffWarrantyAccessForStatus()}
                                />
                                <div
                                  className={`font-bold content-center text-sm ${
                                    !hasStaffWarrantyAccessForStatus() ? "text-gray-400" : ""
                                  }`}
                                >
                                  Approved
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Radio
                                  id="rejected"
                                  name="statusId"
                                  value={WarrantyStatusEnum.Rejected.toString()}
                                  checked={
                                    Number(warrantyFormData.statusId) ===
                                    WarrantyStatusEnum.Rejected
                                  }
                                  onChange={handleChangeInput}
                                  disabled={!hasStaffWarrantyAccessForStatus()}
                                />
                                <div
                                  className={`font-bold content-center text-sm ${
                                    !hasStaffWarrantyAccessForStatus() ? "text-gray-400" : ""
                                  }`}
                                >
                                  Rejected
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Name:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="filledOutByName"
                                name="filledOutByName"
                                type="text"
                                placeholder="Name..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.filledOutByName ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength
                                    .filledOutByName
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Email:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="filledOutByEmail"
                                name="filledOutByEmail"
                                type="text"
                                placeholder="Email..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.filledOutByEmail ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength
                                    .filledOutByEmail
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg h-[42px]">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Return Method:
                              </div>
                            </div>
                            <div className="flex flex-col lg:flex-row gap-1 lg:gap-5">
                              <div className="flex items-center gap-2">
                                <Radio
                                  id="returnPartsUps"
                                  name="returnPartsUps"
                                  checked={
                                    warrantyFormData.returnPartsUps ?? false
                                  }
                                  onChange={handleChangeInputReturnMethod}
                                />
                                <div className="font-bold content-center text-sm">
                                  UPS
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Radio
                                  id="returnPartsComercial"
                                  name="returnPartsComercial"
                                  checked={
                                    warrantyFormData.returnPartsComercial ??
                                    false
                                  }
                                  onChange={handleChangeInputReturnMethod}
                                />
                                <div className="font-bold content-center text-sm">
                                  Commercial Carrier
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Radio
                                  id="returnPartsCompanyTruck"
                                  name="returnPartsCompanyTruck"
                                  checked={
                                    warrantyFormData.returnPartsCompanyTruck ??
                                    false
                                  }
                                  onChange={handleChangeInputReturnMethod}
                                />
                                <div className="font-bold content-center text-sm">
                                  Company Truck
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Customer Name:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="customerName"
                                name="customerName"
                                type="text"
                                placeholder="Customer Name..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.customerName ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.customerName
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Customer Address:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="customerAddress"
                                name="customerAddress"
                                type="text"
                                placeholder="Customer Address..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.customerAddress ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength
                                    .customerAddress
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Customer City:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="customerCity"
                                name="customerCity"
                                type="text"
                                placeholder="Customer City..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.customerCity ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.customerCity
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Customer State:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="customerState"
                                name="customerState"
                                type="text"
                                placeholder="Customer State..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.customerState ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength
                                    .customerState
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Customer Zip:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="customerZip"
                                name="customerZip"
                                type="text"
                                placeholder="Customer Zip..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.customerZip ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.customerZip
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Date Purchased:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="datePurchase"
                                name="datePurchase"
                                type="date"
                                placeholder="Date..."
                                onChange={handleChangeInput}
                                value={formatDateInputs(
                                  warrantyFormData.datePurchase ?? ""
                                )}
                                sizing="md"
                                pattern="\d{4}-\d{2}-\d{2}"
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Date Failure:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="dateFailure"
                                name="dateFailure"
                                type="date"
                                placeholder="Date..."
                                onChange={handleChangeInput}
                                value={formatDateInputs(
                                  warrantyFormData.dateFailure ?? ""
                                )}
                                sizing="md"
                                pattern="\d{4}-\d{2}-\d{2}"
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Date Of Repair:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="dateOfRepair"
                                name="dateOfRepair"
                                type="date"
                                placeholder="Date..."
                                onChange={handleChangeInput}
                                value={formatDateInputs(
                                  warrantyFormData.dateOfRepair ?? ""
                                )}
                                sizing="md"
                                pattern="\d{4}-\d{2}-\d{2}"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Attachments Added:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="attachmentsAdded"
                                name="attachmentsAdded"
                                type="text"
                                placeholder="Attachments Added..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.attachmentsAdded ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength
                                    .attachmentsAdded
                                }
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex bg-yellow-200">
                              <div className="font-bold text-black content-center">
                                Serial Number:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="serialNumber"
                                name="serialNumber"
                                type="text"
                                placeholder="Serial Number..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.serialNumber ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.serialNumber
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Manufacturer:
                              </div>
                            </div>
                            <div className="flex">
                              <div className="w-full">
                                <Select
                                  id="manufacturerId"
                                  name="manufacturerId"
                                  onChange={handleChangeSelect}
                                  value={warrantyFormData.manufacturerId ?? ""}
                                  sizing="md"
                                  required
                                >
                                  <option value={""}>(Select)</option>
                                  {manufacturerData &&
                                    manufacturerData.map((manufacturer) => (
                                      <option
                                        key={manufacturer.id}
                                        value={manufacturer.id}
                                      >
                                        {manufacturer.name} (
                                        {manufacturer.rimssOemvendorNumber})
                                      </option>
                                    ))}
                                </Select>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Model:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="model"
                                name="model"
                                type="text"
                                placeholder="Model..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.model ?? ""}
                                sizing="md"
                                maxLength={warrantyClaimStringFieldMaxLength.model}
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Acres/Hrs Operated:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="acresHrsOperated"
                                name="acresHrsOperated"
                                type="text"
                                placeholder="Acres/Hrs Operated..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.acresHrsOperated ?? ""}
                                sizing="md"
                                maxLength={10}
                                onKeyDown={(event) => {
                                  if (onlyNumberInput(event)) {
                                    event.preventDefault();
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Tractor Make:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="tractorMake"
                                name="tractorMake"
                                type="text"
                                placeholder="Tractor Make..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.tractorMake ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.tractorMake
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Tractor Model:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="tractorModel"
                                name="tractorModel"
                                type="text"
                                placeholder="Tractor Model..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.tractorModel ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.tractorModel
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Description:
                              </div>
                            </div>
                            <div className="w-full">
                              <Textarea
                                id="description"
                                name="description"
                                placeholder="Description..."
                                onChange={handleChangeTextArea}
                                value={warrantyFormData.description ?? ""}
                                rows={3}
                                required
                                minLength={WARRANTY_DESCRIPTION_MIN_LENGTH}
                                maxLength={WARRANTY_DESCRIPTION_MAX_LENGTH}
                              />
                              <p className="text-sm text-gray-500 mt-1">
                                {(warrantyFormData.description ?? "").length} /{" "}
                                {WARRANTY_DESCRIPTION_MAX_LENGTH} characters
                                (minimum {WARRANTY_DESCRIPTION_MIN_LENGTH})
                              </p>
                              {descriptionError && (
                                <p className="text-red-600 text-sm mt-1">
                                  {descriptionError}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Selling Dealer Name:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="sellingDealerName"
                                name="sellingDealerName"
                                type="text"
                                placeholder="Selling Dealer Name..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.sellingDealerName ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength
                                    .sellingDealerName
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Selling Dealer Address:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="sellingDealerAddress"
                                name="sellingDealerAddress"
                                type="text"
                                placeholder="Selling Dealer Address..."
                                onChange={handleChangeInput}
                                value={
                                  warrantyFormData.sellingDealerAddress ?? ""
                                }
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength
                                    .sellingDealerAddress
                                }
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Selling City:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="sellingCity"
                                name="sellingCity"
                                type="text"
                                placeholder="Selling City..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.sellingCity ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.sellingCity
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Selling State:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="sellingState"
                                name="sellingState"
                                type="text"
                                placeholder="Selling State..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.sellingState ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.sellingState
                                }
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-[35%_65%] px-0 text-sm lg:px-5 md:text-md lg:text-lg">
                            <div className="flex">
                              <div className="font-bold content-center">
                                Selling Zip:
                              </div>
                            </div>
                            <div className="w-full">
                              <TextInput
                                id="sellingZip"
                                name="sellingZip"
                                type="text"
                                placeholder="Selling Zip..."
                                onChange={handleChangeInput}
                                value={warrantyFormData.sellingZip ?? ""}
                                sizing="md"
                                maxLength={
                                  warrantyClaimStringFieldMaxLength.sellingZip
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </fieldset>
                    <fieldset className="mt-10">
                      <legend className="text-black font-bold bg-gray-100 w-full rounded-t-md px-1">
                        Requested Parts Replaced
                      </legend>
                      <hr className="bg-gray-300 h-[2px]"></hr>
                      <div className="flex px-5 pt-5">
                        <Button
                          disabled={buttonDisabled}
                          type="button"
                          className="buttonMain"
                          onClick={() => editWarrantyPartsReplaceds(null)}
                        >
                          Add
                        </Button>
                      </div>
                      <div>
                        <WarrantyPartsTable
                          data={warrantyPartsList}
                          editWarrantyPartsReplaceds={
                            editWarrantyPartsReplaceds
                          }
                          deleteWarrantyPart={deleteWarrantyPart}
                        />
                      </div>
                    </fieldset>
                    <fieldset className="mt-10">
                      <legend className="text-black font-bold bg-gray-100 w-full rounded-t-md px-1">
                        Requested Labor
                      </legend>
                      <hr className="bg-gray-300 h-[2px]"></hr>
                      <div className="flex px-5 pt-5">
                        <Button
                          disabled={buttonDisabled}
                          type="button"
                          className="buttonMain"
                          onClick={() => editWarrantyLabor(null)}
                        >
                          Add
                        </Button>
                      </div>
                      <div>
                        <WarrantyLaborTable
                          data={warrantyLaborList}
                          editWarrantyLabor={editWarrantyLabor}
                          deleteWarrantyLabor={deleteWarrantyLabor}
                        />
                      </div>
                    </fieldset>
                    <fieldset className="mt-10">
                      <legend className="text-black font-bold bg-gray-100 w-full rounded-t-md px-1">
                        Images
                      </legend>
                      <hr className="bg-gray-300 h-[2px]"></hr>
                      <div className="flex px-5 pt-5">
                        <Button
                          disabled={buttonDisabled}
                          type="button"
                          className="buttonMain"
                          onClick={() => editWarrantyPhoto(null)}
                        >
                          Add
                        </Button>
                      </div>
                      <div>
                        <WarrantyPhotosTable
                          data={warrantyPhotoList}
                          editWarrantyPhoto={editWarrantyPhoto}
                          deleteWarrantyPhoto={deleteWarrantyPhoto}
                        />
                      </div>
                    </fieldset>
                    {id && hasStaffWarrantyAccess() && (
                      <fieldset className="mt-10">
                        <legend className="text-black font-bold bg-gray-100 w-full rounded-t-md px-1">
                          Change history
                        </legend>
                        <hr className="bg-gray-300 h-[2px]"></hr>
                        <WarrantyChangeLogPanel entries={changeLogEntries} />
                      </fieldset>
                    )}
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      disabled={buttonDisabled}
                      type="submit"
                      className="buttonMain"
                    >
                      Save
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <EditWarrantyPartModal
        show={showWarrantyPartModal}
        setShow={setShowWarrantyPartModal}
        warrantyPart={selectedWarrantyPart ?? null}
        addUpdateWarrantyPart={addUpdateWarrantyPart}
      />
      <EditWarrantyLaborModal
        show={showWarrantyLaborModal}
        setShow={setShowWarrantyLaborModal}
        warrantyLabor={selectedWarrantyLabor ?? null}
        addUpdateWarrantyLabor={addUpdateWarrantyLabor}
      />
      <EditWarrantyPhotoModal
        show={showWarrantyPhotoModal}
        setShow={setShowWarrantyPhotoModal}
        warrantyPhoto={selectedWarrantyPhoto ?? null}
        addUpdateWarrantyPhoto={addUpdateWarrantyPhoto}
      />
    </div>
  );
}
